import PlanBadge from '@/components/subscription/PlanBadge';
import { Avatar, brandAvatarTint } from '@/components/ui/avatar';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@/components/ui/menu';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { Button, Flex, Heading, Span, Text, VStack } from '@chakra-ui/react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'iconoir-react';
import { NAV_DARK_FOCUS_RING } from '../layout/nav-dark';

type MenuItems = {
  value: string;
  labelKey: string;
  action: 'settings' | 'integrations' | 'billing';
  badge?: React.ReactNode;
};

const UserMenu = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const brandId = user?.currentBrandId;
  const { data: subscription } = useSubscriptionQuery({
    scopeKey: brandId,
    enabled: !!brandId,
  });

  const menuItems: MenuItems[] = [
    {
      value: 'account-settings',
      labelKey: 'userMenu.accountSettings',
      action: 'settings' as const,
    },
    {
      value: 'billing',
      labelKey: 'userMenu.myPlan',
      badge:
        brandId && subscription?.planId ? (
          <PlanBadge planId={subscription.planId} size="xs" />
        ) : undefined,
      action: 'billing' as const,
    },
    { value: 'integrations', labelKey: 'userMenu.integrations', action: 'integrations' as const },
  ];

  const handleMenuClick = (action: 'settings' | 'integrations' | 'billing') => {
    if (action === 'settings') {
      navigate('/settings/account/profile');
    } else if (action === 'billing') {
      // Navigate to subscription settings page
      navigate('/settings/billing');
    } else if (action === 'integrations') {
      // Navigate to integrations settings page
      navigate('/settings/integrations');
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);

      toaster.create({
        title: t('userMenu.logoutToast.title'),
        description: t('userMenu.logoutToast.description'),
        type: 'info',
        duration: 2000,
      });

      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);

      toaster.create({
        title: t('userMenu.logoutError.title'),
        description: t('userMenu.logoutError.description'),
        type: 'error',
        duration: 4000,
      });

      setIsLoggingOut(false);
    }
  };

  return (
    <Flex align="center" gap={2}>
      {/* User Profile Avatar */}

      {/* User Dropdown */}
      <MenuRoot>
        <MenuTrigger asChild>
          <Button
            variant="ghost"
            rounded="full"
            ml={1}
            p={0}
            w="36px"
            minW="36px"
            h="36px"
            color="white"
            _hover={{ bg: 'announcement.controlHoverBg' }}
            _open={{ bg: 'announcement.controlHoverBg' }}
            _focusVisible={NAV_DARK_FOCUS_RING}
          >
            <Span>
              <Avatar
                colorPalette="primary"
                w="32px"
                h="32px"
                borderWidth="1.5px"
                borderColor="navDark.avatarRing"
                src={user?.avatar?.fileUrl}
                name={user?.firstName ?? ''}
              />
            </Span>
          </Button>
        </MenuTrigger>
        <MenuContent minW={250} rounded="xl">
          <Flex align="center" gap={3} py={2} px={2}>
            <Avatar
              size="sm"
              src={user?.avatar?.fileUrl}
              name={user?.firstName ?? ''}
              {...brandAvatarTint}
            />
            <VStack align="flex-start" gap={0} lineHeight="shorter" minW={0}>
              <Heading size="sm">{user?.firstName}</Heading>
              <Text fontSize="xs" color="fg.muted" truncate maxW="170px">
                {user?.email}
              </Text>
            </VStack>
          </Flex>
          <MenuSeparator borderColor="border" />
          <VStack align="stretch" gap={0.5}>
            {menuItems.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                cursor="pointer"
                justifyContent="space-between"
                onClick={() => handleMenuClick(item.action)}
              >
                <Text fontSize="sm" fontWeight="medium" flex={1}>
                  {t(item.labelKey)}
                </Text>
                {item.badge && <Span>{item.badge}</Span>}
              </MenuItem>
            ))}
          </VStack>
          <MenuSeparator borderColor="border" />
          <MenuItem
            value="logout"
            onClick={handleLogout}
            cursor="pointer"
            _highlighted={{ bg: 'red.50' }}
            justifyContent="space-between"
            color="red.fg"
            disabled={isLoggingOut}
            opacity={isLoggingOut ? 0.6 : 1}
          >
            <Text fontSize="sm" fontWeight="medium">
              {isLoggingOut ? t('userMenu.loggingOut') : t('userMenu.logout')}
            </Text>
            <LogOut width={18} height={18} />
          </MenuItem>
        </MenuContent>
      </MenuRoot>
    </Flex>
  );
};

export default UserMenu;
